import { getCollection } from '@adornis/baseql/server/collections.js';
import { Exam, type ExamState } from '../../exam.js';
import { dispatchJobs, dispatchJobsAndWait } from '../jobs/dispatch.js';

export const reprocessErroredSubmissionsToolDef = [
  {
    type: 'function',
    function: {
      name: 'reprocess_errored_submissions',
      description:
        'Versucht, die fehlgeschlagenen Schülerabgaben erneut zu extrahieren. Nutze dieses Tool ausschließlich, wenn der Nutzer die erneute Extraktion starten möchte.',
      parameters: {
        type: 'object',
        properties: {},
        required: [],
      },
    },
  },
];

export async function handleReprocessErroredSubmissions(exam: Exam, _toolCallArgs: any): Promise<object> {
  const examId = String(exam._id ?? '');
  if (!examId) return { content: 'Fehler: Keine Klausur-ID.' };

  const examCollection = await getCollection(Exam as any);
  const examDoc = await examCollection.findOne({ _id: examId } as any);
  if (!examDoc) return { content: 'Fehler: Klausur nicht gefunden.' };

  const erroredIds: string[] = Array.isArray((examDoc as any).examState?.answerExtractionProgress?.errorSubmissions)
    ? [...(examDoc as any).examState.answerExtractionProgress.errorSubmissions]
    : [];

  if (!erroredIds.length) {
    return { content: 'SYSTEM INFO: Keine fehlgeschlagenen Abgaben vorhanden.' };
  }

  // ONE DISPATCHER FOR BOTH SIZES. A single submission used to run the step inline instead — outside the
  // queue, so with no lease, no retry and no LLM gate, and it wrote COMPLETED on the progress record itself
  // even when a batch for the same run was still live. It is a job now like every other submission; the only
  // difference a single one makes is that the agent can afford to wait for it and report in this turn.
  //
  // `retry` changes nothing about the work. It keeps these rows out of the dedupe key of a first attempt that
  // may still be live; there is no run for them to join, since the step stays open simply by having live rows.
  // Closing and announcing follows the last of them, which is exactly what the inline path could not do.
  if (erroredIds.length === 1) {
    const { outcome } = await dispatchJobsAndWait('answerExtraction', examId, {
      submissionIds: erroredIds,
      retry: true,
    });
    if (outcome === 'done') return { content: 'SYSTEM INFO: Die Abgabe wurde erfolgreich erneut extrahiert.' };
    if (outcome === 'failed') {
      return {
        content:
          'SYSTEM INFO: Die erneute Extraktion ist fehlgeschlagen. Du kannst es erneut versuchen oder der Nutzer muss die Antwortbereiche selbst markieren. Entschuldige dich für die Unannehmlichkeiten!',
      };
    }
    // Still queued. Reporting this as a failure would tell the teacher to retry work that is already running.
    return {
      content:
        'SYSTEM INFO: Die erneute Extraktion läuft noch im Hintergrund. Du wirst benachrichtigt, sobald sie abgeschlossen ist.',
    };
  }

  // Several submissions: too long to hold the turn open, so dispatched and left to announce itself. They
  // retry independently and in parallel, each under its own submission lock, rather than in one detached
  // sequential call that lost everything still pending if the process went down mid-run.
  await dispatchJobs('answerExtraction', examId, { submissionIds: erroredIds, retry: true });

  return {
    content: `SYSTEM INFO: Die erneute Extraktion von ${erroredIds.length} Abgaben wurde im Hintergrund gestartet. Du wirst benachrichtigt, sobald sie abgeschlossen ist.`,
  };
}

export function reprocessErroredSubmissionsAvailability(examState: ExamState): boolean {
  const errorSubmissions = (examState as any)?.answerExtractionProgress?.errorSubmissions;
  return Array.isArray(errorSubmissions) && errorSubmissions.length > 0;
}
